// externals
import type { Reducer } from 'redux';
import {
    SET_FIELD_ERROR,
    CLEAR_ENTITY_ERRORS,
    SET_ERROR_FIELDS,
    SET_RECOMMENDED_FIELDS,
    SET_ERROR_FIELDS_BY_CATEGORY,
    CLEAR_CATEGORY_ERRORS
} from '../actionTypes/dealScreenErrorActionTypes';
import { TAB_CHANGE } from '../actionTypes/baseDealActionTypes';
import { DealTabs, FeesCategoriesType } from '@makemydeal/dr-shared-types';

const FEE_CATEGORIES = Object.values(FeesCategoriesType);

export type FieldErrors = { [fieldName: string]: boolean };
export type CategoryFieldErrors = { [categoryName: string]: Array<FieldErrors> };

export type EntityErrors = FieldErrors | CategoryFieldErrors;
export type RecommendationErrors = FieldErrors | CategoryFieldErrors;

export interface EntityErrorState {
    errors: EntityErrors;
    recommendations: RecommendationErrors;
    accumulatedErrors: EntityErrors;
    errorCount: number;
    recommendationCount: number;
    visited?: boolean; // Optional field to track if the entity has been visited
    isActive?: boolean;
}

type DealScreenErrorState = {
    [entity: string]: EntityErrorState;
};

const getDefaultEntityState = (): EntityErrorState => {
    return {
        errors: {},
        recommendations: {},
        accumulatedErrors: {},
        errorCount: 0,
        recommendationCount: 0,
        visited: false, // Initialize visited to false
        isActive: false
    };
};

export const countErrors = (errors: EntityErrors, countNestedObjects: boolean = false): number => {
    return Object.values(errors).reduce((count, value) => {
        if (typeof value === 'boolean') {
            return count + (value ? 1 : 0);
        }

        if (Array.isArray(value)) {
            return (
                count +
                value.reduce((arrayCount, row) => {
                    if (!row || row.isDeleted) return arrayCount;
                    return arrayCount + Object.values(row).filter(Boolean).length;
                }, 0)
            );
        }

        if (countNestedObjects && typeof value === 'object' && value !== null) {
            return count + countErrors(value as EntityErrors, true);
        }

        return count;
    }, 0);
};

const filterActiveErrors = (errorFields: FieldErrors) => {
    return (
        errorFields &&
        Object.keys(errorFields).reduce((filteredFields, key) => {
            if (!errorFields[key] || key === 'isDeleted') {
                filteredFields[key] = errorFields[key];
            }
            return filteredFields;
        }, {} as FieldErrors)
    );
};

const filterActiveCategoryErrors = (categoryErrors: Array<FieldErrors>): Array<FieldErrors> => {
    return categoryErrors.map((errorRow) => filterActiveErrors(errorRow));
};

const mergeCategoryErrors = (
    existingCategoryErrors: Array<FieldErrors>,
    newCategoryErrors: Array<FieldErrors>,
    category: string,
    deleteIndex: number
): Array<FieldErrors> => {
    const result: Array<FieldErrors> = [];

    // For FEES component, When deleteIndex is specified; filter the deleted row from existing errors to match with component state
    if (FEE_CATEGORIES.includes(category as FeesCategoriesType) && deleteIndex >= 0) {
        existingCategoryErrors = existingCategoryErrors.filter((_, rowIndex) => rowIndex !== deleteIndex);
    }

    // Ensure we process all rows (existing and new)
    const maxLength = Math.max(existingCategoryErrors.length, newCategoryErrors.length);

    for (let i = 0; i < maxLength; i++) {
        const existingRow = existingCategoryErrors[i] || {};
        const newRow = newCategoryErrors[i] || {};

        // Merge row: preserve true values from existing, add new values
        const mergedRow: FieldErrors = { ...existingRow };

        //Preserve any true values from existing row
        Object.keys(newRow).forEach((field) => {
            // when a deleted row is restored, clear all errors
            if ((existingRow['isDeleted'] && !newRow['isDeleted']) || newRow[field] === false) {
                mergedRow[field] = false;
            } else if (newRow[field] === true) {
                mergedRow[field] = true;
            }
        });

        if ('isDeleted' in newRow) {
            mergedRow['isDeleted'] = newRow['isDeleted'];
        }
        result.push(mergedRow);
    }

    return result;
};

const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export const DEAL_SCREEN_ERROR_INITIAL_STATE: DealScreenErrorState = {
    [DealTabs.SUMMARY]: {
        ...getDefaultEntityState(),
        isActive: true
    }
};

export type DealScreenErrorReducer = Reducer<DealScreenErrorState>;

export const reducer: Reducer<DealScreenErrorState> = (
    state: DealScreenErrorState = DEAL_SCREEN_ERROR_INITIAL_STATE,
    action: any
) => {
    switch (action.type) {
        case SET_FIELD_ERROR: {
            const { entity, field, error, ispaymentError, skipAccumulation } = action.payload;
            const entityState = state[entity] || getDefaultEntityState();

            // Determine whether the main errors should be updated for this field
            const shouldUpdateMainErrors = !error || ispaymentError || entity === DealTabs.SUMMARY;

            let updatedErrors;
            // When skipAccumulation is true (Generate Contracts flow), update errors immediately
            if (skipAccumulation) {
                updatedErrors = { ...entityState.errors, [field]: error };
            } else {
                updatedErrors = shouldUpdateMainErrors ? { ...entityState.errors, [field]: error } : entityState.errors;
            }

            const accumulatedErrors = entityState.accumulatedErrors;

            return {
                ...state,
                [entity]: {
                    ...entityState,
                    errors: updatedErrors,
                    accumulatedErrors,
                    errorCount: countErrors(updatedErrors)
                }
            };
        }
        case SET_ERROR_FIELDS: {
            const { entity, errorFields, skipAccumulation } = action.payload;
            const entityState = state[entity] || getDefaultEntityState();
            // When skipAccumulation is true (Generate Contracts flow), update errors immediately
            let updatedErrors;
            if (skipAccumulation) {
                updatedErrors = {
                    ...entityState.errors,
                    ...errorFields
                };
            } else {
                updatedErrors = {
                    ...entityState.errors,
                    ...(entityState.isActive ? filterActiveErrors(errorFields) : errorFields)
                };
            }

            const accumulatedErrors = { ...entityState.accumulatedErrors, ...errorFields };

            return {
                ...state,
                [entity]: {
                    ...entityState,
                    errors: updatedErrors,
                    accumulatedErrors,
                    errorCount: countErrors(updatedErrors, true)
                }
            };
        }
        case SET_RECOMMENDED_FIELDS: {
            const { entity, recommendedFields } = action.payload;
            const entityState = state[entity] || getDefaultEntityState();
            const updatedRecommendations: RecommendationErrors = {
                ...entityState.recommendations,
                ...recommendedFields
            };
            return {
                ...state,
                [entity]: {
                    ...entityState,
                    recommendations: updatedRecommendations,
                    recommendationCount: countErrors(updatedRecommendations)
                }
            };
        }
        case TAB_CHANGE: {
            const { activeTab } = action.payload;
            const previousEntity = Object.keys(state).find((entity) => state[entity]?.isActive);
            const accumulatedErrorsCopy = previousEntity ? deepClone(state[previousEntity].accumulatedErrors) : {};

            return {
                ...state,
                [activeTab]: {
                    ...(state[activeTab] || getDefaultEntityState()),
                    isActive: true
                },
                ...(previousEntity
                    ? {
                          [previousEntity]: {
                              ...state[previousEntity],
                              isActive: false,
                              errors: accumulatedErrorsCopy,
                              errorCount: countErrors(accumulatedErrorsCopy)
                          }
                      }
                    : {})
            };
        }
        case CLEAR_ENTITY_ERRORS: {
            const { entity } = action.payload;

            const entityState = state[entity] || getDefaultEntityState();
            return {
                ...state,
                [entity]: {
                    ...entityState,
                    errors: {},
                    accumulatedErrors: {},
                    errorCount: 0
                }
            };
        }
        case SET_ERROR_FIELDS_BY_CATEGORY: {
            const { entity, category, categoryErrors, skipCategoryMerge, deleteIndex, skipAccumulation } = action.payload;
            const entityState = state[entity] || getDefaultEntityState();

            let updatedCategoryErrors: Array<FieldErrors>;

            // Always accumulate original categoryErrors
            const existingAccumulatedErrors = (entityState.accumulatedErrors as CategoryFieldErrors)[category] || [];
            const mergedCategoryAccumulatedErrors = mergeCategoryErrors(
                existingAccumulatedErrors,
                categoryErrors,
                category,
                deleteIndex
            );
            const accumulatedErrors: EntityErrors = {
                ...entityState.accumulatedErrors,
                [category]: mergedCategoryAccumulatedErrors
            };

            if (skipAccumulation) {
                updatedCategoryErrors = mergedCategoryAccumulatedErrors;
            } else if (skipCategoryMerge || !entityState.isActive) {
                // Skip processing when merge is not needed or entity is not active
                updatedCategoryErrors = categoryErrors;
            } else {
                // Get existing category errors for this category
                const existingCategoryErrors = (entityState.errors as CategoryFieldErrors)[category] || [];

                // Filter new categoryErrors to keep only false values
                const filteredCategoryErrors = filterActiveCategoryErrors(categoryErrors);

                // Merge existing true values with new filtered values (preserves existing errors)
                updatedCategoryErrors = mergeCategoryErrors(existingCategoryErrors, filteredCategoryErrors, category, deleteIndex);
            }

            // Apply active entity logic with merged data
            const updatedErrors: EntityErrors = {
                ...entityState.errors,
                [category]: updatedCategoryErrors
            };

            return {
                ...state,
                [entity]: {
                    ...entityState,
                    errors: updatedErrors,
                    accumulatedErrors,
                    errorCount: countErrors(updatedErrors)
                }
            };
        }
        case CLEAR_CATEGORY_ERRORS: {
            const { entity, category } = action.payload;
            const entityState = state[entity] || getDefaultEntityState();

            // Set the category to an empty array instead of removing it
            const updatedErrors = {
                ...entityState.errors,
                [category]: []
            };

            const updatedAccumulatedErrors = {
                ...entityState.accumulatedErrors,
                [category]: []
            };

            return {
                ...state,
                [entity]: {
                    ...entityState,
                    errors: updatedErrors,
                    accumulatedErrors: updatedAccumulatedErrors,
                    errorCount: countErrors(updatedErrors)
                }
            };
        }
        default:
            return state;
    }
};
